// widget/src/hooks/useOpenTriggers.ts
//
// External ways to open/close Pearl, so links, menus, buttons and custom JS
// can drive the widget. Three surfaces, all bridging to the widget's own
// open()/close():
//   1. Hash    — #pearl (or #ask-pearl) opens it. Matches the nav-finder
//                convention (#finder*). Fires on initial load AND hashchange,
//                and the hash is cleared on open so the same link re-fires.
//   2. Click   — any element with [data-pearl-open] (or an <a href="#pearl">)
//                opens it. Capture-phase, so it also works on themes that
//                preventDefault hash links.
//   3. JS API  — window.Pearl.open() / window.Pearl.close().
//
// Cross-page links (e.g. /pages/oem#pearl) are handled by the initial-hash
// check after navigation; same-page anchors by the click/hashchange paths.

import { useEffect, useRef } from "react"

const OPEN_HASHES = new Set(["#pearl", "#ask-pearl"])
const TRIGGER_SELECTOR =
  "[data-pearl-open], a[href='#pearl'], a[href='#ask-pearl']"

declare global {
  interface Window {
    Pearl?: { open: () => void; close: () => void }
  }
}

interface Options {
  onOpen: () => void
  onClose: () => void
}

export function useOpenTriggers({ onOpen, onClose }: Options): void {
  // Keep the latest callbacks in refs so the listeners (registered once) never
  // call a stale closure — close() in particular changes identity each render.
  const onOpenRef = useRef(onOpen)
  const onCloseRef = useRef(onClose)
  onOpenRef.current = onOpen
  onCloseRef.current = onClose

  useEffect(() => {
    if (typeof window === "undefined") return

    const clearHash = () => {
      try {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        )
      } catch {
        window.location.hash = ""
      }
    }

    const openFromHash = () => {
      if (OPEN_HASHES.has(window.location.hash.toLowerCase())) {
        onOpenRef.current()
        clearHash()
      }
    }

    // 1. Hash — initial load + subsequent changes.
    openFromHash()
    const onHashChange = () => openFromHash()
    window.addEventListener("hashchange", onHashChange)

    // 2. Click delegation (capture phase).
    const onClick = (e: MouseEvent) => {
      const start = e.target as Element | null
      const el = start?.closest<HTMLElement>(TRIGGER_SELECTOR)
      if (!el) return
      // Same-page anchors/buttons: open inline rather than letting the hash
      // navigate (which would otherwise leave #pearl lingering in the URL).
      e.preventDefault()
      onOpenRef.current()
      clearHash()
    }
    document.addEventListener("click", onClick, true)

    // 3. Imperative API.
    window.Pearl = {
      open: () => onOpenRef.current(),
      close: () => onCloseRef.current(),
    }

    return () => {
      window.removeEventListener("hashchange", onHashChange)
      document.removeEventListener("click", onClick, true)
      delete window.Pearl
    }
  }, [])
}
